<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ProjectEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    project: {
      type: Object,
      required: false,
    },
    target: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getPayload = (...args) => {
    const [data] = args;

    if (!props.target) {
      return data;
    }

    const fieldsToSubmit = targetConfig.value[props.target].fieldsToSubmit;
    if (fieldsToSubmit.indexOf('*') > -1) {
      return data;
    }

    return _.pick(data, fieldsToSubmit);
  };

  const formId = ref(null);
  const defaultTitle = ref(
    $fn.value.pgettext('Project', 'Edit General Information')
  );
  const targetConfig = ref({
    general: {
      title: $fn.value.pgettext('Project', 'Edit general data'),
      fieldsToSubmit: ['*'],
    },
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'md',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    defaultTitle: defaultTitle.value,
    targetConfig: targetConfig.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'defaultTitle' },
      { name: 'targetConfig' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId },
    ux: {},
    dataRefs: {
      formId,
      defaultTitle,
      targetConfig,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: props.target
          ? targetConfig.value[props.target].title
          : defaultTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.project,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('project');
    }
  );
  watch(
    () => props.target,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('target');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_l2n36h6fe_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_tk2ewujzo_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_mkut42h33_getActionArgs = async ($event) => {
    return {
      objectType: 'project',
      op: 'upsert',
      data: getPayload(props.project),
    };
  };

  const act_7315092382398562_closeModal_l2n36h6fe =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_l2n36h6fe_getActionArgs,
    });
  const inst_evh_7315092382398562_close_e3tcvo095 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_l2n36h6fe,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_e3tcvo095],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_58fj1ffg5 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_pizsz1umt =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_58fj1ffg5,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_pizsz1umt],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_62jdkxcjq =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_qrl4c6wpx =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_62jdkxcjq,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_qrl4c6wpx],
    event: 'close',
  });
  const act_1040167445267876_closeModal_tk2ewujzo =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_tk2ewujzo_getActionArgs,
    });
  const inst_evh_1040167445267876_success_vhih5560e =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_tk2ewujzo,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_mkut42h33 = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_mkut42h33_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_vhih5560e],
  });
  const inst_evh_1040167445267876_submit_89tivaoop =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_mkut42h33,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_89tivaoop],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_tx9684to8 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_urpoy04n8 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_tx9684to8,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_urpoy04n8],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_e3tcvo095,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_pizsz1umt,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_qrl4c6wpx,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_vhih5560e,
    inst_evh_1040167445267876_submit_89tivaoop,
    save: inst_evh_1040167445267876_submit_89tivaoop,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_urpoy04n8,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_e3tcvo095 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_e3tcvo095.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_pizsz1umt = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_pizsz1umt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_qrl4c6wpx = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_qrl4c6wpx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_vhih5560e = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_vhih5560e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_89tivaoop = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_89tivaoop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_urpoy04n8 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_urpoy04n8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ProjectEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="project"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <FieldsetProjectGeneral
            :project="project"
            :formId="formId"
            :target="target"
          >
          </FieldsetProjectGeneral>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.project || !forms.project.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
